import React, { useEffect, useState } from "react";
import "./timeline.css";

function Timeline() {
  const [showScrollHint, setShowScrollHint] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in");
          }
        });
      },
      { threshold: 0.2 }
    );

    const items = document.querySelectorAll(".content-item");
    items.forEach((item) => {
      observer.observe(item);
    });

    return () => {
      items.forEach((item) => {
        observer.unobserve(item);
      });
    };
  }, []);

  // Hide scroll hint after user starts scrolling
  useEffect(() => {
    if (!showScrollHint) return; // Already hidden

    const viewer = document.querySelector('.brandbook-viewer');
    if (!viewer) return;

    let hasScrolled = false;

    const handleScroll = () => {
      if (!hasScrolled) {
        hasScrolled = true;
        setShowScrollHint(false);
      }
    };

    // Listen for wheel events (mouse wheel, trackpad)
    const handleWheel = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        // Horizontal scroll detected
        handleScroll();
      }
    };

    // Listen for touch events (mobile)
    let touchStartX = 0;
    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
    };
    const handleTouchMove = (e) => {
      const touchEndX = e.touches[0].clientX;
      if (Math.abs(touchEndX - touchStartX) > 10) {
        handleScroll();
      }
    };

    // Try to detect scroll on the iframe
    const iframe = viewer.querySelector('iframe');
    if (iframe) {
      iframe.onload = () => {
        try {
          const iframeWindow = iframe.contentWindow;
          if (iframeWindow) {
            iframeWindow.addEventListener('scroll', handleScroll, true);
            iframeWindow.addEventListener('wheel', handleWheel, true);
          }
        } catch (e) {
          // CORS issue - use alternative methods
        }
      };
    }

    // Add event listeners
    viewer.addEventListener('wheel', handleWheel, { passive: true });
    viewer.addEventListener('touchstart', handleTouchStart, { passive: true });
    viewer.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      viewer.removeEventListener('wheel', handleWheel);
      viewer.removeEventListener('touchstart', handleTouchStart);
      viewer.removeEventListener('touchmove', handleTouchMove);
      if (iframe && iframe.contentWindow) {
        try {
          iframe.contentWindow.removeEventListener('scroll', handleScroll, true);
          iframe.contentWindow.removeEventListener('wheel', handleWheel, true);
        } catch (e) {
          // Ignore CORS errors
        }
      }
    };
  }, [showScrollHint]);

  return (
    <div className="timeline-container">
      <div className="timeline-content">
        {/* My Design Process - The Story */}
        <div className="content-item fade-in" id="design-process">
          <h3>My Design Process</h3>
          <p className="case-study-text">
            Starting with <strong className="highlight">mid fidelity prototypes</strong>, I tested <strong className="highlight">core functionality</strong> and <strong className="highlight">user flows</strong> to understand how divers would interact with the platform. Through iterative design, I focused on creating <strong className="highlight">intuitive experiences</strong> that would make dive logging and social sharing seamless.
          </p>

          <div className="image-text-layout">
            <img
              src="/assets/projects/snapper/createPost.png"
              alt="Snapper post creation flow"
            />
            <div>
              <h4 style={{ fontFamily: "var(--font-heading)", fontSize: "1.2rem", fontWeight: "600", color: "var(--color-primary)", marginBottom: "var(--spacing-sm)", marginTop: "0" }}>Post Creation Flow</h4>
              <p>
                I developed the complete <strong className="highlight">workflow</strong> for creating dive posts, allowing users to log dives, add photos, and tag locations. The <strong className="highlight">design decision</strong> to prioritize photos over text enables divers to quickly capture their experience while details are fresh, creating a more engaging and immediate logging experience.
              </p>
            </div>
          </div>

          <div className="image-text-layout">
            <div>
              <h4 style={{ fontFamily: "var(--font-heading)", fontSize: "1.2rem", fontWeight: "600", color: "var(--color-primary)", marginBottom: "var(--spacing-sm)", marginTop: "0" }}>Fish Tagging Feature</h4>
              <p>
                I designed the <strong className="highlight">fish identification and tagging system</strong>, a key differentiator that transforms each dive into an <strong className="highlight">educational experience</strong>. The <strong className="highlight">visual search</strong> interface makes marine life identification accessible to divers of all experience levels, addressing the problem of divers not knowing what they're seeing.
              </p>
            </div>
            <img
              src="/assets/projects/snapper/fishInfo.png"
              alt="Snapper fish tagging feature"
            />
          </div>

          <p style={{ marginTop: "var(--spacing-xl)", textAlign: "center" }}>
            <a 
              href="https://www.figma.com/proto/CnMflOHC5UyqlNlSZLo8Ze/Snapper?node-id=2714-5005&p=f&t=h3DUElHg3cvSqooO-0&scaling=scale-down&content-scaling=fixed&page-id=2016%3A565&starting-point-node-id=2714%3A5005&show-proto-sidebar=1" 
              target="_blank"
              rel="noopener noreferrer"
              className="prototype-link"
            >
              View Interactive Prototype ↗
            </a>
          </p>
        </div>

        {/* Impact on Users */}
        <div className="content-item fade-in" id="user-impact">
          <h3>How My Design Impacted Users</h3>
          <p className="case-study-text">
            The design enables divers to easily log and relive their diving experiences through an <strong className="highlight">intuitive interface</strong>. The <strong className="highlight">fish tagging feature</strong> I developed allows users to quickly identify and learn about marine life, turning each dive into an <strong className="highlight">educational experience</strong>.
          </p>
          <p className="case-study-text">
            The social feed creates a sense of community, helping divers discover new dive sites and connect with others who share their passion. By prioritizing visual content and quick logging, divers can capture their experiences immediately after a dive while details are fresh, significantly improving <strong className="highlight">user engagement</strong> and satisfaction.
          </p>
        </div>

        {/* Business Value */}
        <div className="content-item fade-in" id="business-value">
          <h3>Why It Matters to the Business</h3>
          <p className="case-study-text">
            Snapper addresses a growing market of recreational divers seeking community and education. By combining social networking with dive logging and marine life education, the app creates multiple <strong className="highlight">engagement touchpoints</strong> that support <strong className="highlight">user retention</strong>.
          </p>
          <p className="case-study-text">
            The <strong className="highlight">intuitive interface</strong> reduces barriers to entry for new users, while the educational component adds significant value beyond simple logging. The design supports long-term engagement through social connections and educational content, creating a platform that users return to not just to log dives, but to learn, discover, and connect.
          </p>
        </div>

        {/* Key Design Decisions */}
        <div className="content-item fade-in" id="key-decisions">
          <h3>What Decisions Made the Biggest Difference</h3>
          
          <div style={{ marginTop: "var(--spacing-lg)" }}>
            <h4 style={{ fontFamily: "var(--font-heading)", fontSize: "1.2rem", fontWeight: "600", color: "var(--color-primary)", marginBottom: "var(--spacing-sm)" }}>Post Creation Flow: Speed Over Complexity</h4>
            <p className="case-study-text">
              I designed a streamlined <strong className="highlight">workflow</strong> that prioritizes photos and quick capture over detailed text entry. This <strong className="highlight">design decision</strong> allows divers to log experiences immediately after a dive while details are fresh, significantly impacting <strong className="highlight">user engagement</strong> and completion rates.
            </p>
          </div>

          <div style={{ marginTop: "var(--spacing-xl)" }}>
            <h4 style={{ fontFamily: "var(--font-heading)", fontSize: "1.2rem", fontWeight: "600", color: "var(--color-primary)", marginBottom: "var(--spacing-sm)" }}>Fish Tagging: Education as a Core Feature</h4>
            <p className="case-study-text">
              My design for the <strong className="highlight">fish identification and tagging system</strong> uses <strong className="highlight">visual search</strong> and tagging, making marine life identification accessible to divers of all experience levels. This <strong className="highlight">educational component</strong> adds significant value beyond simple logging, addressing the problem of divers not knowing what they're seeing and creating a unique differentiator in the market.
            </p>
          </div>

          <div style={{ marginTop: "var(--spacing-xl)" }}>
            <h4 style={{ fontFamily: "var(--font-heading)", fontSize: "1.2rem", fontWeight: "600", color: "var(--color-primary)", marginBottom: "var(--spacing-sm)" }}>Social Feed: Discovery Through Browsing</h4>
            <p className="case-study-text">
              Our team's collaborative design creates a Pinterest-style browsing experience that encourages exploration and discovery of dive sites and marine life. This approach transforms the app from a simple logging tool into a platform for learning and community building.
            </p>
          </div>
        </div>

        {/* Brand Identity */}
        <div className="content-item fade-in" id="brand">
          <h3>Brand Identity</h3>
          <p className="case-study-text">
            I contributed to developing Snapper's <strong className="highlight">brand identity</strong>, creating a cohesive <strong className="highlight">visual language</strong> that reflects the underwater world while maintaining a modern, approachable aesthetic aligned with our brand values.
          </p>
          <div className="brandbook-container" style={{ marginTop: "var(--spacing-xl)" }}>
            <div className="brandbook-wrapper">
              {showScrollHint && (
                <div 
                  className="brandbook-scroll-hint"
                  onClick={() => setShowScrollHint(false)}
                  style={{ cursor: 'pointer' }}
                >
                  <span>Scroll horizontally to explore all pages</span>
                </div>
              )}
              <div className="brandbook-viewer">
                <iframe
                  src="/assets/projects/snapper/brandbook.pdf#toolbar=0&navpanes=0&scrollbar=0&zoom=68"
                  width="100%"
                  height="100%"
                  className="brandbook-embed"
                  title="Snapper Brandbook"
                  scrolling="no"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Timeline;
